/**
 * Kind/MIME de mídia: o que o canal aceita, o `accept` do input, rótulos e a
 * validação client-side (docs/06 + docs/09 §1.4/§3.2).
 *
 * Os tipos oferecidos vêm SEMPRE de `capabilities.media_types` (RF-UX-120);
 * este módulo só mapeia kind -> `accept` do input e valida tamanho contra
 * `LIMITS.MEDIA_UPLOAD_MAX_BYTES` (16 MB, fonte: `@zapir/shared`).
 */
import { ERROR_CODES, LIMITS, type MediaInfo } from "@zapir/shared";
import type { ChannelCapabilities } from "@zapir/shared";
import { ZapirApiError } from "./errors";

export type MediaKind = ChannelCapabilities["media_types"][number];

/** Kinds que trafegam binário pelo `POST /api/media/uploads`. */
export type UploadableKind = "image" | "video" | "audio" | "voice" | "document" | "sticker";

export type { MediaInfo };

/** `accept` do `<input type=file>` por kind (docs/09 RF-UX-047). */
export const MEDIA_ACCEPT: Record<UploadableKind, string> = {
  image: "image/*",
  video: "video/*",
  audio: "audio/*",
  voice: "audio/*",
  document: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z",
  // Aceita qualquer imagem: a conversao para webp acontece antes do upload.
  sticker: "image/*",
};

const KIND_LABEL: Record<UploadableKind, string> = {
  image: "Foto",
  video: "Vídeo",
  audio: "Áudio",
  voice: "Mensagem de voz",
  document: "Documento",
  sticker: "Figurinha",
};

export function kindLabel(kind: UploadableKind): string {
  return KIND_LABEL[kind];
}

export function toUploadable(kind: MediaKind): UploadableKind | null {
  switch (kind) {
    case "image":
    case "video":
    case "audio":
    case "voice":
    case "document":
    case "sticker":
      return kind;
    default:
      // location/contact não trafegam arquivo.
      return null;
  }
}

/** Inferência do kind pelo MIME real; cai para o kind pedido quando genérico. */
export function kindFromFile(file: Blob, requested: UploadableKind): UploadableKind {
  const mime = file.type.toLowerCase();
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return requested === "voice" ? "voice" : "audio";
  return requested;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const text = value >= 10 ? String(Math.round(value)) : value.toFixed(1).replace(".", ",");
  return `${text} ${units[unit]}`;
}

export function formatLimit(): string {
  return formatBytes(LIMITS.MEDIA_UPLOAD_MAX_BYTES);
}

/**
 * Validação client-side antes do upload (docs/05: anexo acima do limite é
 * rejeitado antes do upload). Erros carregam `code` canônico do shared:
 * - oversize -> VALIDATION (details.reason = MEDIA_TOO_LARGE);
 * - tipo fora de `media_types` -> CHANNEL_CAPABILITY_UNSUPPORTED.
 */
export function validateFile(
  file: Blob,
  fileName: string,
  capabilities: ChannelCapabilities,
  /** Kind pedido no menu de anexo; sem ele, deduz pelo MIME. */
  preferKind?: UploadableKind,
): UploadableKind {
  void fileName;
  const allowed = new Set<MediaKind>(capabilities.media_types);
  const mime = file.type.toLowerCase();
  let kind: UploadableKind | null = null;
  // Figurinha e imagem tem o mesmo MIME: so o pedido do menu separa as duas.
  if (preferKind === "sticker" && allowed.has("sticker")) kind = "sticker";
  else if (mime.startsWith("image/")) kind = allowed.has("image") ? "image" : null;
  else if (mime.startsWith("video/")) kind = allowed.has("video") ? "video" : null;
  else if (mime.startsWith("audio/")) {
    kind = allowed.has("voice") ? "voice" : allowed.has("audio") ? "audio" : null;
  } else {
    kind = allowed.has("document") ? "document" : null;
  }
  if (!kind) {
    throw new ZapirApiError("CHANNEL_CAPABILITY_UNSUPPORTED", 422, { mime });
  }
  if (file.size > LIMITS.MEDIA_UPLOAD_MAX_BYTES) {
    throw new ZapirApiError("VALIDATION", 413, {
      reason: "MEDIA_TOO_LARGE",
      maxBytes: LIMITS.MEDIA_UPLOAD_MAX_BYTES,
    });
  }
  if (file.size === 0) {
    throw new ZapirApiError("VALIDATION", 422, { reason: "MEDIA_EMPTY" });
  }
  return kind;
}

/** Copy de erro de mídia: canônica do shared + detalhe operacional. */
export function mediaErrorCopy(err: unknown): string {
  if (err instanceof ZapirApiError) {
    if (err.code === "VALIDATION" && isRecord(err.details)) {
      if (err.details.reason === "MEDIA_TOO_LARGE") {
        return `Arquivo maior que ${formatLimit()}. Comprima ou escolha outro arquivo.`;
      }
      if (err.details.reason === "MEDIA_EMPTY") {
        return "O arquivo está vazio. Escolha outro arquivo.";
      }
    }
    return err.copy;
  }
  return ERROR_CODES.SERVER_ERROR.copy;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
